import axios from "axios";
import {format} from "date-fns";
import {Destination as RegistrationDestination } from "@/utils/registration";
import {resolveApiBaseUrl} from "@/utils/apiBaseUrl";

const baseURL = resolveApiBaseUrl({
    nodeEnv: process.env.NODE_ENV,
    buildApiUrl: process.env.VUE_APP_API_URL,
    runtimeConfig: window.__APP_CONFIG__,
});

axios.defaults.baseURL = baseURL;
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
axios.defaults.headers.common['Access-Control-Allow-Methods'] = 'GET, POST, OPTIONS';
axios.defaults.headers.common['Access-Control-Allow-Headers'] = 'X-Requested-With, Content-Type, Authorization, Accept, Cookie';
axios.interceptors.request.use(function (config) {
    const token = localStorage.getItem('jwt')
    if (config.headers.Authorization === undefined) {
        config.headers.Authorization = 'Bearer ' + token;
    }

    return config;
});

const actions = {
    loginAction(context, payload) {
        return new Promise((resolve, reject) => {
            axios.post(
                'login',
                payload
            ).then(async (response) => {
                localStorage.clear()
                this.commit('REMOVE_USER_LIST')
                const token = response.data.token

                const parseJwt = () => {
                    try {
                        return JSON.parse(atob(token.split('.')[1]));
                    } catch (e) {
                        return null;
                    }
                };
                const parsedJwt = parseJwt()
                if (parsedJwt === null) {
                    reject('could not parse jwt')
                    return
                }
                const userUuid = parsedJwt.userUuid

                // check if user requires password change
                const passwordChangeRequiredResponse = await axios.get(
                    'users/' + userUuid + '/passwordChangeRequired',
                    {
                        headers: {
                            Authorization: 'Bearer ' + token
                        }
                    }
                );
                const passwordChangeRequired = !!passwordChangeRequiredResponse.data;

                this.commit('SET_LOGIN')
                this.commit('SET_JWT', token)
                this.commit('SET_USER_UUID', userUuid)
                this.commit('SET_USER_PASSWORD_CHANGE_REQUIRED', passwordChangeRequired)
                this.commit('CLEAR_CAMPAIGNS')
                resolve(true)
            })
                .catch(() => {
                    reject('wrong credentials.')
                })
        })
    },
    getWeekOrder(context, params) {
        const userUuid = this.getters.getUserUuid
        return new Promise((resolve, reject) => {
            axios.get(
                'users/' + userUuid + '/order',
                {
                    params: params
                }
            ).then((response) => {
                resolve(response.data)
            }).catch(() => {
                reject('Session expired.')
                this.commit('REMOVE_LOGIN')
                window.location.reload()
            })
        })
    },
    loadCampaigns(context, userUuid) {
        const targetUserUuid = userUuid || this.getters.getUserUuid

        return new Promise((resolve) => {
            axios.get(
                'users/' + targetUserUuid + '/campaigns'
            ).then((response) => {
                const campaigns = Array.isArray(response?.data?.campaigns)
                    ? response.data.campaigns
                    : []
                this.commit('SET_CAMPAIGNS', campaigns)
                this.commit('SET_CAMPAIGNS_LOADED', true)
                resolve(campaigns)
            }).catch(() => {
                this.commit('SET_CAMPAIGNS', [])
                this.commit('SET_CAMPAIGNS_LOADED', true)
                resolve([])
            })
        })
    },
    getUserList(context, userUuid) {
        return new Promise((resolve, reject) => {
            axios.get(
                'users/' + userUuid
            ).then((response) => {
                this.commit('SET_USER_LIST', response.data)
                resolve(response.data)
            })
                .catch(() => {
                    reject('Session expired.')
                    this.commit('REMOVE_LOGIN')
                    window.location.reload()
                })
        })
    },
    putOrder(context, order) {
        const userUuid = this.getters.getUserUuid
        return axios.put(
            'users/' + userUuid + '/order',
            {
                orderDays: order
            }
        ).then((response) => {
            return response.data
        }).catch((error) => {
            if (error.response.status === 403) {
                this.commit('REMOVE_LOGIN')
                window.location.reload()
                return
            }

            return error.response.data
        })
    },
    postRegistration(context, form) {
        return new Promise((resolve, reject) => {
            axios.post(
                'registration',
                form
            ).then((response) => {
                resolve(response.data)
            }).catch(() => {
                reject('Session expired.')
            })
        })
    },
    changePassword(context, payload) {
        let userUuid = this.getters.getUserUuid
        return new Promise((resolve, reject) => {
            axios.post(
                'users/' + userUuid + '/password',
                payload
            ).then(async (response) => {

                // recheck if user requires password change
                const passwordChangeRequiredResponse = await axios.get(
                    'users/' + userUuid + '/passwordChangeRequired'
                );
                const passwordChangeRequired = passwordChangeRequiredResponse.data !== false;
                this.commit('SET_USER_PASSWORD_CHANGE_REQUIRED', passwordChangeRequired)

                resolve(response.data)
            }).catch(error => {
                reject(error.response.data)
            })
        })
    },
    logout() {
        this.commit('REMOVE_LOGIN')
        this.commit('REMOVE_JWT')
        this.commit('CLEAR_CAMPAIGNS')
    },
    requestAccountDeletionAndLogout() {
        const userUuid = this.getters.getUserUuid
        return new Promise((resolve, reject) => {
            axios.post(
                `users/${userUuid}/requestAccountDeletion`,
            ).then(() => {
                this.commit('REMOVE_LOGIN')
                this.commit('REMOVE_JWT')
                this.commit('CLEAR_CAMPAIGNS')
                resolve()
            }).catch(() => {
                reject()
            })
        })
    },
    loadPublicMenu(context, params) {
        const dateFrom = format(params.dateFrom, 'yyyy-MM-dd');
        const dateTo = format(params.dateTo, 'yyyy-MM-dd');


        return new Promise((resolve, reject) => {
            axios.get(
                `public/menu/${params.clientId}/${params.menuId}/${dateFrom}/${dateTo}`,
            ).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                if (error.response) {
                    reject(error.response.data)
                } else if (error.request) {
                    reject('No response from server.')
                } else {
                    reject(error.message)
                }
            })
        })
    },
    loadRegistrationFormOptions(context, payload) {
        let queryData;
        if (payload.registrationConfigurationId) {
            queryData = `registrationConfigurationId=${payload.registrationConfigurationId}`
        } else if (payload.destination === RegistrationDestination.Home) {
            queryData = 'registrationType=home'
        } else if (payload.destination === RegistrationDestination.Work) {
            queryData = 'registrationType=company'
        }

        return new Promise((resolve, reject) => {
            axios.get(
                `registration/registrationFormOptions?${queryData}`,

            ).then((response) => {
                if (
                    typeof response.data !== 'object'
                    || typeof response.data.type !== 'string'
                    || typeof response.data.institutionName !== 'string'
                    || false === Array.isArray(response.data.institutionGroups)
                    || typeof response.data.salutationOptions !== 'object'
                    || false === Array.isArray(response.data.salutationOptions.billingAddress)
                    || false === Array.isArray(response.data.salutationOptions.child)
                    || false === Array.isArray(response.data.salutationOptions.deliveryAddress)
                ) {
                    reject('Received unexpected response from server.')
                    return
                }

                resolve(response.data)
            }).catch((error) => {
                if (error.response) {
                    reject(error.response.data)
                } else if (error.request) {
                    reject('No response from server.')
                } else {
                    reject(error.message)
                }
            })
        })
    },
    sendRegistrationRequest(context, payload) {
        const formData = {}
        if (payload.registrationConfigurationId) {
            formData['registrationConfigurationId'] = payload.registrationConfigurationId
        } else if (payload.destination === RegistrationDestination.Home) {
            formData['registrationType'] = 'home';
        } else if (payload.destination === RegistrationDestination.Work) {
            formData['registrationType'] = 'company';
        }
        formData['registrationData'] = payload.registrationData

        return new Promise((resolve, reject) => {
                axios.post(
                    `registration/sendRegistrationRequest`,
                    formData
                ).then((response) => {
                    resolve(response.data)
                }).catch((error) => {
                    if (error.response) {
                        reject(error.response.data)
                    } else if (error.request) {
                        reject('No response from server.')
                    } else {
                        reject(error.message)
                    }
                })
            })
    },
    confirmRegistration(context, payload) {
        return new Promise((resolve, reject) => {
            axios.post(
                `registration/confirmRegistrationRequest`,
                {
                    mandantId: payload.mandantId,
                    confirmToken: payload.confirmToken
                }
            ).then((response) => {
                resolve(response.data)
            }).catch((error) => {
                if (error.response) {
                    if (error.response.status === 400 && error.response.data.error === 'INVALID_CONFIRM_TOKEN') {
                        reject('INVALID_CONFIRM_TOKEN')
                        return
                    }
                    reject(error.response.data)
                } else if (error.request) {
                    reject('No response from server.')
                } else {
                    reject(error.message)
                }
            })
        })
    },
}

export default actions
